import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  notification,
  Row,
  Col,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Joi from "joi";
import axios from "axios";

const Create = ({ isVisible, handleClose, moduleName, base_url }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [fileList, setFileList] = useState([]);

  const joiOptions = { abortEarly: false };

  // ---------------- VALIDATION ----------------

  const validate = (value) => {
    const schemaObj = {
      name: Joi.string().required().label("Name").messages({
        "string.empty": "Name is required",
        "any.required": "Name is required",
      }),
    };

    const schema = Joi.object(schemaObj).options({ allowUnknown: true });
    const { error } = schema.validate(value, joiOptions);

    const errors = {};
    if (error) {
      error.details.forEach((field) => {
        errors[field.path[0]] = field.message;
      });
    }

    if (!fileList.length) {
      errors.catelog = "PDF is required";
    }

    return errors;
  };

  // ---------------- FILE CHANGE ----------------

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
  };

  // ---------------- SUBMIT ----------------

  const handleSubmit = async (value) => {
    const errors = validate(value);
    setFormError(errors);

    if (Object.keys(errors).length) return;

    const formData = new FormData();

    formData.append("name", value.name);

    if (fileList[0]) {
      formData.append("catelog", fileList[0].originFileObj);
    }

    setLoading(true);

    try {
      const response = await axios.post(base_url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response?.data?.message) {
        notification.success({
          message: response.data.message,
        });

        form.resetFields();
        setFileList([]);

        handleClose(true);
      } else {
        notification.error({
          message: "Failed to create record",
        });
      }
    } catch (err) {
      console.error(err);

      notification.error({
        message: err?.response?.data?.message || "Something went wrong",
      });
    }

    setLoading(false);
  };

  const beforeUpload = (file) => {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      notification.error({
        message: "Only PDF files are allowed",
      });
      return Upload.LIST_IGNORE;
    }

    const isUnder100MB = file.size / 1024 / 1024 <= 100;

    if (!isUnder100MB) {
      notification.error({
        message: "File must be smaller than 100MB",
      });
      return Upload.LIST_IGNORE;
    }

    return false; // prevents auto upload
  };

  return (
    <Modal
      title={`Add ${moduleName}`}
      visible={isVisible}
      onCancel={() => handleClose(false)}
      okText="Create"
      cancelText="Cancel"
      confirmLoading={loading}
      onOk={() => {
        form
          .validateFields()
          .then((values) => handleSubmit(values))
          .catch(() => {});
      }}
      width={600}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Catalogue Name"
              name="name"
              required
              {...(formError.name && {
                help: formError.name,
                validateStatus: "error",
              })}
            >
              <Input placeholder="Enter Catalogue name" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="Upload PDF"
              extra="Only PDF allowed • Max size 100MB"
              required
              {...(formError.catelog && {
                help: formError.catelog,
                validateStatus: "error",
              })}
            >
              <Upload
                beforeUpload={beforeUpload}
                fileList={fileList}
                onChange={handleFileChange}
                accept=".pdf"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select PDF</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Create;
